import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, Sparkles, MapPin, CalendarDays } from "lucide-react";
import api, { fileUrl, img } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Pill } from "@/components/Bits";

export default function Home() {
  const { t, L } = useLang();
  const [markets, setMarkets] = useState([]);
  const [series, setSeries] = useState([]);

  useEffect(() => {
    api.get("/markets").then((r) => setMarkets(r.data)).catch(() => {});
    api.get("/series").then((r) => setSeries(r.data)).catch(() => {});
  }, []);

  const upcoming = markets.find((m) => m.status === "upcoming") || markets[0];

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-14 pb-8 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <Pill color="#00E5FF">{t("hero_tag")}</Pill>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
              className="font-display text-5xl sm:text-6xl lg:text-7xl mt-5 leading-[0.95]"
            >
              {t("hero_title")}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}
              className="mt-6 text-lg text-black/70 max-w-md leading-relaxed"
            >
              {t("hero_sub")}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
              className="mt-8 flex flex-wrap gap-3"
            >
              <Link to="/markets" data-testid="hero-explore" className="fk-border rounded-full px-6 py-3 font-semibold bg-[#FFD93D] fk-shadow fk-lift flex items-center gap-2">
                {t("explore_markets")} <ArrowRight size={18} />
              </Link>
              <Link to="/vendor-register" data-testid="hero-vendor" className="fk-border rounded-full px-6 py-3 font-semibold bg-white fk-lift">
                {t("become_vendor")}
              </Link>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.1 }}
            className="relative"
          >
            <div className="absolute -top-4 -left-4 w-24 h-24 bg-[#FF5757] rounded-2xl fk-border -z-0 hidden sm:block" />
            <img
              src="https://images.unsplash.com/photo-1563902341721-029085ad9347?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"
              alt="market"
              className="relative rounded-2xl fk-border-thick fk-shadow-lg w-full h-[340px] sm:h-[420px] object-cover"
            />
            <div className="absolute -bottom-5 -right-2 bg-[#00E5FF] fk-border rounded-xl px-4 py-2 font-mono-fk text-sm fk-shadow rotate-[-4deg]">
              <Sparkles size={14} className="inline mr-1" /> 60+ {L("摊位", "stalls")}
            </div>
          </motion.div>
        </div>

        <div className="border-y-2 border-black bg-black text-[#FFD93D] py-3 mt-4">
          <Marquee speed={40} gradient={false}>
            {series.concat(series).map((s, i) => (
              <span key={i} className="font-display text-2xl mx-8">
                {L(s.title_zh, s.title_en)} <span className="text-[#00E5FF]">✳</span>
              </span>
            ))}
            {series.length === 0 && <span className="font-display text-2xl mx-8">放空生活集 · FANGKONG LIFE MARKET ✳</span>}
          </Marquee>
        </div>
      </section>

      {/* Upcoming market */}
      {upcoming && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
          <div className="font-mono-fk text-xs tracking-widest text-[#FF5757] mb-3">{t("upcoming").toUpperCase()}</div>
          <div className="grid lg:grid-cols-2 gap-8 items-stretch">
            <img src={img(upcoming.cover_image)} alt={upcoming.title_en} className="rounded-2xl fk-border-thick w-full h-72 lg:h-full object-cover" />
            <div className="bg-white fk-border-thick rounded-2xl p-8 fk-shadow flex flex-col justify-between">
              <div>
                <h3 className="font-display text-3xl sm:text-4xl">{L(upcoming.title_zh, upcoming.title_en)}</h3>
                <p className="mt-4 text-black/70 leading-relaxed">{L(upcoming.description_zh, upcoming.description_en)}</p>
                <div className="mt-6 space-y-2 font-mono-fk text-sm">
                  <div className="flex items-center gap-2"><CalendarDays size={16} /> {upcoming.date}</div>
                  <div className="flex items-center gap-2"><MapPin size={16} /> {L(upcoming.location_zh, upcoming.location_en)}</div>
                </div>
              </div>
              <Link to={`/markets/${upcoming.id}`} className="mt-6 inline-flex items-center gap-2 fk-border rounded-full px-5 py-2.5 font-semibold bg-[#FFD93D] fk-lift w-fit">
                {t("view_detail")} <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Series preview */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-8">
        <div className="flex items-end justify-between mb-8">
          <h2 className="font-display text-4xl">{t("about_title")}</h2>
          <Link to="/about" className="font-mono-fk text-sm underline underline-offset-4">{t("read_more")}</Link>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {series.slice(0, 2).map((s, i) => (
            <motion.div
              key={s.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="group bg-white fk-border-thick rounded-2xl overflow-hidden fk-lift"
            >
              <img src={img(s.cover_image)} alt={s.title_en} className="w-full h-52 object-cover border-b-2 border-black" />
              <div className="p-6">
                <Pill>{t("series_label")}</Pill>
                <h3 className="font-display text-2xl mt-3">{L(s.title_zh, s.title_en)}</h3>
                <p className="mt-2 text-black/70 text-sm line-clamp-3">{L(s.intro_zh, s.intro_en)}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="bg-[#FF5757] text-white fk-border-thick rounded-3xl p-10 sm:p-14 text-center fk-shadow-lg">
          <h2 className="font-display text-4xl sm:text-5xl">{L("想摆摊？加入放空！", "Want a stall? Join Fangkong!")}</h2>
          <p className="mt-4 text-white/90 max-w-xl mx-auto">{L("注册摊主账号，管理你的专页，申请参与每一场市集。", "Register a vendor account, manage your page and apply to every market.")}</p>
          <Link to="/vendor-register" className="mt-7 inline-flex fk-border border-white rounded-full px-7 py-3 font-semibold bg-white text-black fk-lift">
            {t("join_market")}
          </Link>
        </div>
      </section>
    </div>
  );
}
